import {create} from "zustand/react";
import {createJSONStorage, persist} from "zustand/middleware";
import {AlertColor} from "@mui/material/Alert/Alert";
import {DeletesBO, useCallDeletes} from "@/service/common.s";
import * as React from "react";
import {AuthButtonVO} from "@/service/menu.s";
import {SlotDrawerHeaderProps, SlotDrawerProps} from "@/widget/DrawerModal/DrawerModal";
import {Breakpoint} from "@mui/system/createBreakpoints/createBreakpoints";
import {ExecuteBO} from "@/service/instance.s";

export type ToastType = {
    message: string;
    variant?: "standard" | "filled" | "outlined";
    open?: boolean;
    severity?: AlertColor;
    duration?: number;
    onClose?: () => void;
};

export type CnipaConnectType = {
    agencyName: string;
    agencyId: string;
    status: "FAIL" | "SUCCESS";
}

enum GlobalEnum {
    CnipaConnect = "cnipaConnect",
}

export type ModeType = "dialog" | "drawer"

export type DrawerModalType = {
    open: boolean;
    header?: string | React.ReactNode;
    body?: React.ReactNode;
    execute?: ExecuteBO;
    width?: number | undefined | null
    id?: number;
    btns?: AuthButtonVO[];
    write?: null | "add" | "edit";
    dialogBtnCallback?: (btn: AuthButtonVO) => void;
    onSearch?: (params?: any) => void;
} & SlotDrawerProps & SlotDrawerHeaderProps;

export const defaultDrawerModal: DrawerModalType = {
    open: false,
    title: "",
    instanceId: undefined,
    businessId: undefined,
    write: null,
    execute: {},
    hiddenFooter: false,
    custom: false,
    headerHeight: 54,
}

export type DialogModalType = {
    open: boolean;
    header?: string | React.ReactNode;
    body?: React.ReactNode;
    id?: number;
    maxWidth?: Breakpoint;
    btns?: AuthButtonVO[];
    write?: "add" | "edit";
    onSearch?: (params?: any) => void;
};

type GlobalStore = {
    toast: ToastType;
    drawerModal: DrawerModalType;
    dialogModal: DialogModalType;
    cnipaConnect: CnipaConnectType;
    actions: {
        setToast: (toast: ToastType) => void;
        setDrawerModal: (drawerModal: DrawerModalType) => void;
        setDialogModal: (drawerModal: DialogModalType) => void;
        setCnipaConnect: (cnipaConnect: CnipaConnectType) => void;
    };
};

const useGlobalStore = create<GlobalStore>()(
    persist(
        (set) => ({
            toast: {message: '', open: false, duration: 2, variant: "filled", severity: "success"},
            drawerModal: defaultDrawerModal,
            dialogModal: {open: false, maxWidth: 'sm'},
            cnipaConnect: {} as CnipaConnectType,
            actions: {
                setCnipaConnect: (cnipaConnect: CnipaConnectType) => {
                    set(() => ({
                        cnipaConnect,
                    }));
                },
                setToast: (toast: ToastType) => {
                    set((state) => ({
                        toast: {...state.toast, ...toast},
                    }));
                },
                setDialogModal: (dialogModal: DialogModalType) =>
                    set((state) => ({
                        dialogModal: {...state.dialogModal, ...dialogModal},
                    })),
                setDrawerModal: (drawerModal: DrawerModalType) =>
                    set((state) => ({
                        drawerModal: {...state.drawerModal, ...drawerModal},
                    })),
            },
        }),
        {
            name: "globalStore",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                [GlobalEnum.CnipaConnect]: state.cnipaConnect,
            }),
        },
    ),
);

export const useDeletes = () => {
    const {mutateAsync: callDeletesMutate} = useCallDeletes();
    const deletes = async (data: DeletesBO) => {
        const result = await callDeletesMutate(data);
        if (result.code == 0) {
            return true
        }
        return false
    };
    return deletes;
};

export const useCnipaConnect = () =>
    useGlobalStore((state) => state.cnipaConnect);

export const useDrawerModal = () =>
    useGlobalStore((state) => state.drawerModal);

export const useDialogModal = () =>
    useGlobalStore((state) => state.dialogModal);

export const useGlobalActions = () => useGlobalStore((state) => state.actions);
export const useToast = () => useGlobalStore((state) => state.toast);

export default useGlobalStore;
